import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { PoliticalCampaign, PoliticalCampaignTopic, ExternalApproval } from '@/types/models'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
import { getContext } from '@/services/networks/networksApi'
import { searchParams } from '@/services/core/listSearch'
import { mapValidationWarnings } from '@/services/core/validationWarnings'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

interface ApiRef {
  id: string
  name: string
  objectState: string
  url: string
}

interface ApiAmount {
  currency: string
  amount: number
}

interface ApiTopic {
  id: string
  name: string
  amount: ApiAmount
  payer?: ApiRef
  payerPoliticalEntity?: ApiRef
}

interface ApiPoliticalCampaign {
  id: string
  publicId?: string
  name: string
  description?: string
  objectState: string
  createTime: string
  updateTime: string
  network: ApiRef & { tagId: string }
  politicalEntity: ApiRef
  election: ApiRef
  totalBudget: ApiAmount
  topics: ApiTopic[]
  externalApprovals?: Array<{ system: string; id: string; name: string; data?: Record<string, unknown> }>
  validationWarnings?: Array<{ code: string; text: string; preventsAdServing: boolean; parameters?: Record<string, string> }>
  ownerTeam: ApiRef & { type: string }
}

export interface PoliticalCampaignInput {
  name: string
  description?: string
  politicalEntityId: string
  electionId: string
  totalBudget: number
  currency: string
  topics: PoliticalCampaignTopic[]
  externalApprovals?: ExternalApproval[]
  objectState?: string
}

function toLocal(raw: ApiPoliticalCampaign): PoliticalCampaign {
  return {
    id: raw.id,
    publicId: raw.publicId,
    name: raw.name,
    description: raw.description ?? '',
    objectState: raw.objectState,
    politicalEntityId: raw.politicalEntity.id,
    politicalEntityName: raw.politicalEntity.name,
    electionId: raw.election.id,
    electionName: raw.election.name,
    totalBudget: raw.totalBudget.amount,
    currency: raw.totalBudget.currency,
    topics: raw.topics.map((t) => ({
      id: t.id,
      name: t.name,
      amount: t.amount.amount,
      currency: t.amount.currency,
      payerId: t.payerPoliticalEntity?.id ?? t.payer?.id,
      payerName: t.payerPoliticalEntity?.name ?? t.payer?.name,
    })),
    externalApprovals: raw.externalApprovals ?? [],
    validationWarnings: mapValidationWarnings(raw.validationWarnings),
    ownerTeamId: raw.ownerTeam.id,
    ownerTeamName: raw.ownerTeam.name,
    networkId: raw.network.id,
    networkName: raw.network.name,
    createTime: raw.createTime,
    updateTime: raw.updateTime,
  }
}

function toApiBody(input: PoliticalCampaignInput) {
  return {
    name: input.name,
    description: input.description ?? '',
    politicalEntity: { id: input.politicalEntityId },
    election: { id: input.electionId },
    totalBudget: { currency: input.currency, amount: input.totalBudget },
    topics: input.topics.map((t) => ({
      id: t.id,
      name: t.name,
      amount: { currency: t.currency, amount: t.amount },
      ...(t.payerId ? { payerPoliticalEntity: t.payerId } : {}),
    })),
    ...(input.externalApprovals ? { externalApprovals: input.externalApprovals } : {}),
    ...(input.objectState ? { objectState: input.objectState } : {}),
  }
}

export const PoliticalCampaignsApi = {
  getAll: async (search?: string): Promise<PoliticalCampaign[]> => {
    const params = new URLSearchParams({
      ...searchParams('politicalCampaigns', search),
      context: getContext(),
    })
    const res = await apiFetch(`${API_BASE}/politicalcampaigns?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, `Failed to fetch political campaigns (${res.status})`)
    const data = await res.json()
    const results = toArray<ApiPoliticalCampaign>(data)
    return results.map(toLocal)
  },

  getById: async (id: string): Promise<PoliticalCampaign | null> => {
    const res = await apiFetch(`${API_BASE}/politicalcampaigns/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (res.status === 404) return null
    if (!res.ok) await throwApiError(res, `Failed to fetch political campaign (${res.status})`)
    return toLocal(await res.json())
  },

  /** Anonymous lookup by publicId; the backend only returns campaigns that are publicly visible. */
  getPublicById: async (publicId: string): Promise<PoliticalCampaign | null> => {
    // Anonymous, and the publicId names its own network, but the API still
    // refuses a request with no network context — any valid one will do.
    const context = getContext()
    const query = context ? `?${new URLSearchParams({ context })}` : ''
    const res = await apiFetch(`${API_BASE}/public/politicalcampaigns/${encodeURIComponent(publicId)}${query}`, {
      headers: JSON_HEADERS,
    })
    if (res.status === 404) return null
    if (!res.ok) await throwApiError(res, `Failed to fetch political campaign (${res.status})`)
    return toLocal(await res.json())
  },

  create: async (id: string, input: PoliticalCampaignInput): Promise<PoliticalCampaign> => {
    const res = await apiFetch(`${API_BASE}/politicalcampaigns/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...toApiBody(input) }),
    })
    if (!res.ok) await throwApiError(res, `Failed to create political campaign (${res.status})`)
    return toLocal(await res.json())
  },

  update: async (id: string, input: PoliticalCampaignInput): Promise<PoliticalCampaign> => {
    const res = await apiFetch(`${API_BASE}/politicalcampaigns/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...toApiBody(input) }),
    })
    if (!res.ok) await throwApiError(res, `Failed to update political campaign (${res.status})`)
    return toLocal(await res.json())
  },
}
